import React, { useEffect } from 'react';
import styles from './mini-calendar.module.scss';
import { IGetCalendarCardMonthParams } from 'shared/api/booking/interfaces/get-calendar-card-month.params.interface';
import { businessStore, dayCalendarStore } from 'entities/index';
import { MonthNames, WeekDaysArray } from 'shared/consts/calendar.consts';
import { LeftArrowSvg, RightArrowSvg } from 'shared/svg';
import * as dateFns from 'date-fns';
import { MonthCalendarDayType } from 'shared/api/booking/interfaces/month-calendar-day-type.enum';
import { monthCalendarStore } from 'entities/calendar/model/month-calendar.store';
import { observer } from 'mobx-react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLinks } from 'app/router/consts/app-links.enum';
import { ICalendarDate } from 'entities/calendar/model/calendar-date';

interface IMiniCalendarProps {
  selectedDateHandler: (date: ICalendarDate) => void;
  shouldNavigate?: boolean;
  onlyWorkdaysClickable?: boolean;
  compact?: boolean;
  hideAdjacentMonths?: boolean;
  className?: string;
}

export const MiniCalendar: React.FC<IMiniCalendarProps> = observer(
  ({
    selectedDateHandler,
    shouldNavigate = true,
    onlyWorkdaysClickable = false,
    compact = false,
    hideAdjacentMonths = false,
    className = '',
  }) => {
    const monthCalendar = monthCalendarStore.monthCalendar;
    const currentDate = monthCalendarStore.currentDate;
    const params = useParams();
    const navigate = useNavigate();
    const pointId = businessStore.pointId || params.pointId;

    const fetchCalendarCardInfo = (date: ICalendarDate) => {
      if (pointId) {
        const dto: IGetCalendarCardMonthParams = {
          month: date.month,
          year: date.year,
          pointId,
        };
        monthCalendarStore.fetchCalendarMonthInfo(dto);
      }
    };
    useEffect(() => {
      fetchCalendarCardInfo(dayCalendarStore.getSelectedDate);
    }, [pointId, monthCalendarStore.serviceId, monthCalendarStore.employeeId]);

    const onClickDateHandler = (day: number, type: MonthCalendarDayType) => {
      if (type === MonthCalendarDayType.NOT_IN_CURRENT_MONTH) return;
      if (onlyWorkdaysClickable && type !== MonthCalendarDayType.WORK_DAY) {
        return;
      }
      const selectedDate: ICalendarDate = {
        year: dayCalendarStore.getSelectedDate.year,
        month: dayCalendarStore.getSelectedDate.month,
        day,
      };
      selectedDateHandler(selectedDate);
      dayCalendarStore.setSelectedDate(selectedDate);
      if (shouldNavigate) {
        navigate(AppLinks.DayCalendarLink);
      }
    };

    const changeMonthHandler = (change: number) => {
      let date = dayCalendarStore.getSelectedDateAsDate;
      date = dateFns.addMonths(date, change);
      const resDate: ICalendarDate = {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: 1,
      };
      fetchCalendarCardInfo(resDate);
      dayCalendarStore.selectedDate = resDate;
    };
    const monthsAround = hideAdjacentMonths
      ? [
          {
            change: 0,
            month: dayCalendarStore.getSelectedDate.month,
            year: dayCalendarStore.getSelectedDate.year,
            isSelected: true,
          },
        ]
      : [-1, 0, 1].map((offset) => {
          const d = dateFns.addMonths(
            dayCalendarStore.getSelectedDateAsDate,
            offset
          );
          return {
            change: offset,
            month: d.getMonth(),
            year: d.getFullYear(),
            isSelected: offset === 0,
          };
        });

    return (
      <div
        className={`${styles.mini_calendar__container} ${compact ? styles.compact : ''} ${className}`}
      >
        <div className={styles.container__month_switcher}>
          <div
            className={styles.switcher__arrow}
            onClick={() => changeMonthHandler(-1)}
          >
            <LeftArrowSvg />
          </div>

          {monthsAround.map((m) => (
            <div
              key={m.month + '-' + m.year}
              className={`${styles.switcher__month} ${
                m.isSelected ? styles.current__month : styles.other__month
              }`}
              onClick={() => !m.isSelected && changeMonthHandler(m.change)}
            >
              <span>{MonthNames[m.month]}</span>
              {m.isSelected && (
                <span className={styles.switcher__year}> {m.year}</span>
              )}
            </div>
          ))}

          <div
            className={styles.switcher__arrow}
            onClick={() => changeMonthHandler(1)}
          >
            <RightArrowSvg />
          </div>
        </div>

        <div className={styles.container__calendar}>
          <div className={styles.calendar__weekdays}>
            {WeekDaysArray.map((day) => (
              <div key={day} className={styles.weekdays__day}>
                {day}
              </div>
            ))}
          </div>

          <div className={styles.calendar__days}>
            {monthCalendar.map((day, dayIndex) => {
              const dayNumber = dateFns.getDate(day.currDate);
              const isToday =
                dayNumber === currentDate.day &&
                currentDate.month === dayCalendarStore.selectedDate.month &&
                currentDate.year === dayCalendarStore.selectedDate.year;
              const isSelectedDay =
                dayNumber === dayCalendarStore.selectedDate.day &&
                dayCalendarStore.selectedDate.month ===
                  dateFns.getMonth(day.currDate);

              let dayStyle = '';
              let isNonClickable = false;
              switch (day.type) {
                case MonthCalendarDayType.NOT_IN_CURRENT_MONTH:
                  dayStyle = styles.day__not_current_month;
                  isNonClickable = true;
                  break;
                case MonthCalendarDayType.DAY_OFF:
                  dayStyle = styles.day__day_off;
                  isNonClickable = onlyWorkdaysClickable;
                  break;
                case MonthCalendarDayType.BUSY:
                  dayStyle = styles.day__busy;
                  isNonClickable = onlyWorkdaysClickable;
                  break;
                case MonthCalendarDayType.OLD_DAY:
                  dayStyle = styles.day__old_day;
                  isNonClickable = onlyWorkdaysClickable;
                  break;
                case MonthCalendarDayType.WORK_DAY:
                  break;
              }
              return (
                <div
                  key={dayIndex}
                  className={`${styles.days__day} ${dayStyle} ${
                    isToday ? styles.days__current : ''
                  } ${isSelectedDay ? styles.selected__day : ''} ${
                    isNonClickable ? styles.day__non_clickable : ''
                  }`}
                  onClick={() => {
                    onClickDateHandler(dayNumber, day.type);
                  }}
                >
                  {dayNumber}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);
